import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { sbq, filterDeleted, throwOnError } from '../../shared/utils/supabaseQuery';
import { applyOwnerFields } from '../../shared/utils/applyOwnerFields';
import { getDefaultOverviewItems } from '../../shared/utils/contentItems';
import { reorderByPlacement, type ReorderPlacement } from '../../shared/utils/reorder';
import { lessonsApi } from '../lessons/lessonsApi';
import { resourcesApi } from '../resources/resourcesApi';
import { unitsApi } from '../units/unitsApi';

import {
  Course,
  CourseContent,
  CreateCourseRequest,
  UpdateCourseRequest,
} from '@/shared/types/course';

type MoveCourseOverviewItemArgs = {
  courseId: string;
  itemId: string;
  placement: ReorderPlacement;
};

export const coursesApi = createApi({
  reducerPath: 'coursesApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Course'],
  endpoints: (builder) => ({
    // Get a single course by ID
    getCourseById: builder.query<Course, string>({
      queryFn: sbq(async (sb, courseId) => {
        // Fetch course with owner and units (excluding deleted units)
        const data = throwOnError(
          await sb
            .from('courses')
            .select(
              '*, owner:users(name, avatar_url), units(id, title, description, owner_id, is_public, is_deleted, content, created_at, updated_at, owner:users(name, avatar_url))'
            )
            .eq('id', courseId)
            .single()
        );

        // Filter out deleted units
        const units = filterDeleted((data as any)?.units || []).map((unit: any) => ({
          ...applyOwnerFields(unit),
          content: unit?.content ?? {},
        }));

        // Attach owner_name for frontend compatibility
        return {
          ...applyOwnerFields(data as any),
          units,
          // Ensure content object
          content: (data as any)?.content ?? {},
        } as Course;
      }, 'Course'),
      providesTags: ['Course'],
    }),

    // Get courses owned by the current user
    getMyCourses: builder.query<Course[], string>({
      queryFn: sbq(async (sb, userId) => {
        const data = throwOnError(
          await sb
            .from('courses')
            .select('*, owner:users(name, avatar_url)')
            .eq('owner_id', userId)
            .eq('is_deleted', false)
            .order('updated_at', { ascending: false })
        );

        // Attach owner_name for frontend compatibility
        return (data || []).map((course: any) => ({
          ...applyOwnerFields(course),
          content: course?.content ?? {},
        }));
      }, 'Courses'),
      providesTags: ['Course'],
    }),

    // Get team courses
    getTeamCourses: builder.query<Course[], string>({
      queryFn: sbq(async (sb, userId) => {
        const data = throwOnError(
          await sb
            .from('user_courses_v')
            .select('*')
            .neq('owner_id', userId)
            .eq('has_team_permission', true)
            .order('updated_at', { ascending: false })
        );

        return (data || []).map((course: any) => ({
          ...course,
          content: course?.content ?? {},
        }));
      }, 'Team Courses'),
      providesTags: ['Course'],
    }),

    // Create a new course
    createCourse: builder.mutation<Course, CreateCourseRequest>({
      queryFn: sbq(async (sb, courseData) => {
        const now = new Date().toISOString();

        const content: CourseContent = {
          schemaVersion: '1',
          overview: getDefaultOverviewItems('course'),
        };

        const newCourse = {
          ...courseData,
          content,
          created_at: now,
          updated_at: now,
        };

        const result = throwOnError(await sb.from('courses').insert([newCourse]).select().single());

        return result as Course;
      }, 'Course'),
      invalidatesTags: ['Course'],
    }),

    // Update a course
    updateCourse: builder.mutation<Course, UpdateCourseRequest>({
      queryFn: sbq(async (sb, updateData) => {
        const { id, content, ...fields } = updateData;
        if (!id) throw new Error('Course ID required');

        const now = new Date().toISOString();
        const updateFields: any = { ...fields, updated_at: now };
        if (content) updateFields.content = content;

        const result = throwOnError(
          await sb.from('courses').update(updateFields).eq('id', id).select().single()
        );

        return result as Course;
      }, 'Course'),
      // Use optimistic updates for better UX
      async onQueryStarted(updateData, { dispatch, queryFulfilled }) {
        // Optimistically update the course in cache
        const patchResult = dispatch(
          coursesApi.util.updateQueryData('getCourseById', updateData.id, (draft) => {
            Object.assign(draft, updateData);
            if (updateData.content) {
              if (!draft.content) draft.content = {} as any;
              Object.assign(draft.content as any, updateData.content);
            }
            draft.updated_at = new Date().toISOString();
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
      invalidatesTags: ['Course'],
    }),

    // Delete a course
    deleteCourse: builder.mutation<void, string>({
      queryFn: sbq(async (sb, courseId) => {
        if (!courseId) throw new Error('Course ID required');

        const { error } = await sb.rpc('soft_delete_course', { p_course_id: courseId });
        if (error) throw error;

        return undefined;
      }, 'Course'),
      invalidatesTags: ['Course'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(unitsApi.util.invalidateTags(['Unit']));
          dispatch(lessonsApi.util.invalidateTags(['Lesson']));
          dispatch(resourcesApi.util.invalidateTags(['Resource', 'ResourceList']));
        } catch {
          // delete failed; nothing to invalidate
        }
      },
    }),

    // Specialized mutation for updating course content (optimized for inline editing)
    updateCourseContent: builder.mutation<
      Course,
      {
        id: string;
        content: Partial<import('@/shared/types/course').CourseContent>;
      }
    >({
      queryFn: sbq(async (sb, { id, content }) => {
        if (!id) throw new Error('Course ID required');

        // Get current course to merge content
        const currentCourse = throwOnError(
          await sb.from('courses').select('content').eq('id', id).single()
        );

        const mergedContent = { ...(currentCourse as any).content, ...content };

        const result = throwOnError(
          await sb
            .from('courses')
            .update({
              content: mergedContent,
              updated_at: new Date().toISOString(),
            })
            .eq('id', id)
            .select()
            .single()
        );

        return result as Course;
      }, 'Course'),
      async onQueryStarted({ id, content }, { dispatch, queryFulfilled }) {
        // Optimistically update the course content in cache
        const patchResult = dispatch(
          coursesApi.util.updateQueryData('getCourseById', id, (draft) => {
            if (draft.content) {
              Object.assign(draft.content, content);
            } else {
              draft.content = content as any;
            }
            draft.updated_at = new Date().toISOString();
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
      invalidatesTags: ['Course'],
    }),

    moveCourseOverviewItem: builder.mutation<void, MoveCourseOverviewItemArgs>({
      queryFn: sbq(async (sb, { courseId, itemId, placement }) => {
        const { error } = await sb.rpc('move_course_overview_item', {
          p_course_id: courseId,
          p_item_id: itemId,
          p_position: placement.position,
          p_anchor_id: placement.anchorId,
        });
        if (error) throw error;
        return undefined;
      }, 'Course'),
      invalidatesTags: ['Course'],
      async onQueryStarted({ courseId, itemId, placement }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          coursesApi.util.updateQueryData('getCourseById', courseId, (draft) => {
            draft.content.overview = reorderByPlacement(
              draft.content.overview ?? [],
              itemId,
              placement
            );
            draft.updated_at = new Date().toISOString();
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetCourseByIdQuery,
  useGetMyCoursesQuery,
  useGetTeamCoursesQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useUpdateCourseContentMutation,
  useMoveCourseOverviewItemMutation,
} = coursesApi;
